import {
  DESKTOP_LOW_QUALITY_PROFILE,
  DESKTOP_MIDDLE_QUALITY_PROFILE,
  DESKTOP_HIGH_QUALITY_PROFILE,
  MOBILE_LOW_QUALITY_PROFILE,
  MOBILE_MIDDLE_QUALITY_PROFILE,
  MOBILE_HIGH_QUALITY_PROFILE,
} from './scene/qualityProfile';
import type { SparkRenderSettingsConfig } from './scene/qualityProfile';
export type { SparkRenderSettingsConfig } from './scene/qualityProfile';

export interface SplatEncodingConfig {
  rgbMin: number;
  rgbMax: number;
  lnScaleMin: number;
  lnScaleMax: number;
  sh1Min: number;
  sh1Max: number;
  sh2Min: number;
  sh2Max: number;
  sh3Min: number;
  sh3Max: number;
}

export const defaultSplatEncoding: SplatEncodingConfig = {
  rgbMin: -0.5,
  rgbMax: 1.5,
  lnScaleMin: -12,
  lnScaleMax: 9,
  sh1Min: -1,
  sh1Max: 1,
  sh2Min: -1,
  sh2Max: 1,
  sh3Min: -1,
  sh3Max: 1,
};

export interface TieredRenderSettingsConfig {
  low: SparkRenderSettingsConfig;
  middle: SparkRenderSettingsConfig;
  high: SparkRenderSettingsConfig;
}

export interface ResponsiveRenderSettingsConfig {
  desktop: TieredRenderSettingsConfig;
  mobile: TieredRenderSettingsConfig;
}

export interface DevConfig {
  /** Toggle the reference floor/ceiling grid helpers */
  showReferenceGrid: boolean;
  /** Outline each collision volume or helper mesh with wireframes */
  showCollisionBounds: boolean;
  /** Toggle magenta markers for every sight point */
  showSightMarkers: boolean;
  /** Optional override for the orbit view center */
  orbitCenterOverride: {
    x: number;
    y: number;
    z: number;
  } | null;
  /** Whether to show a helper sphere at the orbit center */
  showOrbitCenterMarker: boolean;
  /** Toggle the teller assistant mesh */
  showTellerAssistant: boolean;
  /** Toggle the rear-view spark overlay */
  showRearView: boolean;
  /**
   * Skip the preloader cinematic (preview-tour auto-rotate + flyover).
   * Splat reveal still runs. Useful when tuning camera params via Unlock
   * — initial mount finalizes instantly; flipping the flag mid-session
   * also collapses the cinematic on the spot.
   */
  skipPreloaderAnimation: boolean;
  /** Keep the preview point-cloud visible; hide splat mesh (debug) */
  keepPreviewModel: boolean;
  /** Show both preview GLB and splat for real-time transform alignment */
  debugSplatTransform: boolean;
  /** Visualize preview-GLB and splat bboxes (centers + full) + axes at origin */
  debugBboxes: boolean;
  /** Artificial delay (seconds) before preview model is considered loaded */
  previewLoadDelaySec: number;
  /** Optional assistant API bearer token */
  assistantApiToken: string;
  /** Spark renderer settings per device class and tier */
  renderSettings: ResponsiveRenderSettingsConfig;
  /** Splat encoding ranges (requires page reload to take effect) */
  splatEncoding: SplatEncodingConfig;
  /** [TEMPORARY] Show mock enter-poi-artifact buttons in chat suggestions */
  mockPoiArtifactButtons: boolean;
  /**
   * Ignore `controls.forcedViewMode` from SceneConfig — сцены, которые
   * жёстко переключают камеру в first-person (Mina, inside-сцены), станут
   * управляемой orbit-камерой. Применяется на следующем mount'е сцены
   * (toggle в этой панели → перезагрузить страницу).
   */
  disableForcedViewMode: boolean;
  /**
   * Разблокировать камеру во время прелоадер-анимации (preview-tour orbit
   * auto-rotate + flyover). Когда `true`: блокировщик ввода не показывается,
   * preview-tour и flyover не запускаются — пользователь может крутить
   * камеру с первого кадра, пока preview-модель и preloader-UI ещё видны.
   * Splat reveal-шейдер играет как обычно. Применяется на следующем mount'е.
   */
  unlockCameraDuringPreloader: boolean;
  /**
   * Разблокировать движение FPS-камеры. По дефолту forced FPS-сцены
   * (Mina, inside-сцены) лочат translation — юзер может только смотреть
   * (drag) и тапать POI. Когда `true`: WASD/стрелки/R/F/пинч-зум начинают
   * двигать камеру. Полезно для тюнинга навигации в интерьере. Применяется
   * на следующем mount'е.
   */
  unlockFpsMovement: boolean;
  /**
   * Снять orbit-ограничения (polar/azimuth-углы, min/maxDistance, panBoundsXZ
   * cage, groundPlane forced-Y target) — камера крутится-вертится свободно
   * для подбора параметров сцены. Применяется ВЖИВУЮ через тот же механизм
   * `controls.suspendOrbitLocks()` / `restoreOrbitLocks()`, что использует
   * прелоадер-цинематика. NB: Y-clamp (`clampMinHeight/MaxHeight/MaxHeightFar`)
   * НЕ снимается — это намеренное ограничение `_locksSuspended` чтобы не
   * ломать FPS-цинематики. Если сцена имеет Y-clamp и его тоже надо снять —
   * отредактируй конфиг сцены вручную.
   */
  unlockCameraConstraints: boolean;
}

export const defaultResponsiveRenderSettings: ResponsiveRenderSettingsConfig = {
  desktop: {
    low: { ...DESKTOP_LOW_QUALITY_PROFILE.renderSettings },
    middle: { ...DESKTOP_MIDDLE_QUALITY_PROFILE.renderSettings },
    high: { ...DESKTOP_HIGH_QUALITY_PROFILE.renderSettings },
  },
  mobile: {
    low: { ...MOBILE_LOW_QUALITY_PROFILE.renderSettings },
    middle: { ...MOBILE_MIDDLE_QUALITY_PROFILE.renderSettings },
    high: { ...MOBILE_HIGH_QUALITY_PROFILE.renderSettings },
  },
};

export const defaultDevConfig: DevConfig = {
  showReferenceGrid: false,
  showCollisionBounds: false,
  showSightMarkers: false,
  orbitCenterOverride: null,
  showOrbitCenterMarker: false,
  showTellerAssistant: false,
  showRearView: false,
  skipPreloaderAnimation: false,
  keepPreviewModel: false,
  debugSplatTransform: false,
  debugBboxes: false,
  previewLoadDelaySec: 0,
  assistantApiToken: 'pil-b564cQPKYzimu9mVDt',
  renderSettings: defaultResponsiveRenderSettings,
  splatEncoding: { ...defaultSplatEncoding },
  mockPoiArtifactButtons: false,
  disableForcedViewMode: false,
  unlockCameraDuringPreloader: false,
  unlockFpsMovement: false,
  unlockCameraConstraints: false,
};
